import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useOrgTreeContext } from '@contexts/org-tree-context';
import { appSlice, dashboardSlice } from '@redux';
import { fetchBriefings, sendBriefingFeedback } from '../services/BriefingService';
import {
  briefingKey,
  enrichmentChips,
  groupTitle,
  IBriefing,
  signalLabel,
  SIGNAL_GROUPS,
} from '../interfaces/IBriefing';
import style from './BriefingsPage.module.scss';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

/** "2026-07-18" -> "18.07.2026" (German date format). */
function formatGerman(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

/** Depth-first search for the node whose orgUnit.orgKey matches. */
function findNode(node: any, orgKey: string): any {
  if (!node) {
    return null;
  }
  if (node.orgUnit && node.orgUnit.orgKey === orgKey) {
    return node;
  }
  for (const child of node.children || []) {
    const found = findNode(child, orgKey);
    if (found) {
      return found;
    }
  }
  return null;
}

/** Build an orgKey -> name map from the whole tree, for showing names instead of IDs. */
function buildNameMap(node: any, map: Map<string, string>): void {
  if (!node) {
    return;
  }
  const key = node.orgUnit && node.orgUnit.orgKey;
  const nm = node.orgUnit && node.orgUnit.name;
  if (key && nm) {
    map.set(key, nm);
  }
  (node.children || []).forEach((c: any) => buildNameMap(c, map));
}

/** Collect the org keys of a node and all of its descendants. */
function collectSubtreeKeys(node: any): Set<string> {
  const keys = new Set<string>();
  const walk = (n: any) => {
    if (!n) {
      return;
    }
    const key = n.orgUnit && n.orgUnit.orgKey;
    if (key) {
      keys.add(key);
    }
    (n.children || []).forEach(walk);
  };
  walk(node);
  return keys;
}

type SeverityFilter = 'ALL' | 'WARN' | 'INFO';
const SEVERITY_ORDER: Record<string, number> = { WARN: 0, INFO: 1, GREEN: 2 };

/**
 * Full-screen "KI-Briefings" page. Shows the exception-based daily briefings for the currently
 * selected organisation AND its children as a searchable, filterable table.
 */
export const BriefingsPage: React.FC = () => {
  const ctx = useOrgTreeContext();
  const orgKey = ctx?.orgKey;
  const orgTree = ctx?.orgTree as any;

  const [date, setDate] = useState<string>(todayIso());
  const [loading, setLoading] = useState(false);

  // On open, jump to the most recent day that actually has briefings (aggregations lag a few days,
  // so literally "today" is usually empty). Probe back up to 8 days.
  useEffect(() => {
    let active = true;
    (async () => {
      for (let i = 0; i <= 8; i++) {
        const d = isoDaysAgo(i);
        try {
          const data = await fetchBriefings(d);
          if (!active) {
            return;
          }
          if (data.length > 0) {
            setDate(d);
            return;
          }
        } catch {
          /* keep probing */
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [allBriefings, setAllBriefings] = useState<IBriefing[]>([]);
  const [search, setSearch] = useState('');
  const [severity, setSeverity] = useState<SeverityFilter>('ALL');
  /** Per-finding feedback state, keyed by briefingKey. */
  const [feedback, setFeedback] = useState<Record<string, 'pending' | 'sent' | 'failed'>>({});
  const dispatch = useDispatch();

  const selectedNode = useMemo(
    () => (orgTree?.rootNode && orgKey ? findNode(orgTree.rootNode, orgKey) : null),
    [orgTree, orgKey]
  );
  const subtreeKeys = useMemo(
    () => (selectedNode ? collectSubtreeKeys(selectedNode) : orgKey ? new Set([orgKey]) : null),
    [selectedNode, orgKey]
  );
  const selectedName = (selectedNode && selectedNode.orgUnit && selectedNode.orgUnit.name) || orgKey;

  const nameMap = useMemo(() => {
    const m = new Map<string, string>();
    if (orgTree?.rootNode) {
      buildNameMap(orgTree.rootNode, m);
    }
    return m;
  }, [orgTree]);
  const orgDisplay = (key: string): string => nameMap.get(key) || key;

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchBriefings(date)
      .then((data) => {
        if (active) {
          setAllBriefings(data);
          setError(null);
        }
      })
      .catch((e) => {
        if (active) {
          setError(String(e && e.message ? e.message : e));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [date]);

  // Briefings under the selected org subtree.
  const inSubtree = useMemo(
    () => (subtreeKeys ? allBriefings.filter((b) => subtreeKeys.has(b.orgKey)) : []),
    [allBriefings, subtreeKeys]
  );
  // Robustness: if nothing falls under the selected org but the day HAS briefings, show them all
  // (with a note) instead of a mysterious empty screen. Happens when the selected org doesn't
  // contain the affected ZSBs or the tree/briefing keys don't line up.
  const usingFallback = inSubtree.length === 0 && allBriefings.length > 0;
  const scoped = usingFallback ? allBriefings : inSubtree;

  // Apply search + severity filter, then sort most-severe first.
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scoped
      .filter((b) => severity === 'ALL' || (b.severity || '').toUpperCase() === severity)
      .filter((b) => {
        if (!q) {
          return true;
        }
        return [
          nameMap.get(b.orgKey) || '',
          b.orgKey,
          b.headline,
          b.reason,
          b.recommendation,
          signalLabel(b.signalType),
        ]
          .filter(Boolean)
          .some((s) => s.toLowerCase().includes(q));
      })
      .sort((a, b) => {
        const sa = SEVERITY_ORDER[(a.severity || '').toUpperCase()] ?? 9;
        const sb = SEVERITY_ORDER[(b.severity || '').toUpperCase()] ?? 9;
        if (sa !== sb) {
          return sa - sb;
        }
        // Same severity: keep one org's findings together, worst-scoring first within the org.
        const byOrg = (a.orgKey || '').localeCompare(b.orgKey || '');
        return byOrg !== 0 ? byOrg : (b.score ?? 0) - (a.score ?? 0);
      });
  }, [scoped, search, severity, nameMap]);

  const counts = useMemo(() => {
    const c = { WARN: 0, INFO: 0 };
    scoped.forEach((b) => {
      const s = (b.severity || '').toUpperCase();
      if (s === 'WARN') c.WARN++;
      else if (s === 'INFO') c.INFO++;
    });
    return c;
  }, [scoped]);

  // Findings grouped for reading: goals the base set itself first, then movements, then volume, then
  // the data caveats. Ranking inside a group is the order `rows` already established.
  const groups = useMemo(() => {
    const titles = [...SIGNAL_GROUPS.map((g) => g.title), 'Weitere Hinweise'];
    return titles
      .map((title) => ({ title, items: rows.filter((b) => groupTitle(b.signalType) === title) }))
      .filter((g) => g.items.length > 0);
  }, [rows]);

  /**
   * Jumps to the base's dashboard. The KPI itself is not pre-selected — the dashboard owns that state
   * — but landing on the right org saves the manual tree navigation the screen otherwise forced.
   */
  const openInDashboard = useCallback(
    (targetOrgKey: string) => {
      dispatch(dashboardSlice.actions.setOrgKey(targetOrgKey));
      dispatch(appSlice.actions.setViewMode('dashboard'));
    },
    [dispatch]
  );

  const rate = useCallback(async (b: IBriefing, verdict: { useful?: boolean; notRelevant?: boolean }) => {
    const key = briefingKey(b);
    setFeedback((current) => ({ ...current, [key]: 'pending' }));
    try {
      await sendBriefingFeedback({
        date: b.date,
        orgKey: b.orgKey,
        signalType: b.signalType,
        kpiId: b.kpiId,
        ...verdict,
      });
      setFeedback((current) => ({ ...current, [key]: 'sent' }));
    } catch {
      setFeedback((current) => ({ ...current, [key]: 'failed' }));
    }
  }, []);

  const sevDot = (s: string) => {
    switch ((s || '').toUpperCase()) {
      case 'WARN':
        return style.dotWarn;
      case 'INFO':
        return style.dotInfo;
      default:
        return style.dotGreen;
    }
  };

  return (
    <div className={style.page} data-testid='briefings-page'>
      <div className={style.head}>
        <h1 className={style.title}>KI-Briefings</h1>
        <div className={style.subtitle}>
          {selectedName} — {formatGerman(date)}
        </div>
      </div>

      <div className={style.toolbar}>
        <label className={style.dateGroup}>
          <span className={style.dateLabel}>Datum</span>
          <input
            className={style.dateInput}
            type='date'
            value={date}
            max={todayIso()}
            onChange={(e) => setDate(e.target.value || todayIso())}
          />
        </label>
        <input
          className={style.search}
          type='search'
          placeholder='Suchen (ZSB, Meldung, Empfehlung …)'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className={style.filters}>
          <button
            type='button'
            className={severity === 'ALL' ? style.chipActive : style.chip}
            onClick={() => setSeverity('ALL')}>
            Alle ({scoped.length})
          </button>
          <button
            type='button'
            className={severity === 'WARN' ? style.chipActive : style.chip}
            onClick={() => setSeverity('WARN')}>
            Warnung ({counts.WARN})
          </button>
          <button
            type='button'
            className={severity === 'INFO' ? style.chipActive : style.chip}
            onClick={() => setSeverity('INFO')}>
            Hinweis ({counts.INFO})
          </button>
        </div>
      </div>

      <div className={style.body}>
        {loading && <div className={style.note}>Briefings werden geladen…</div>}
        {error && <div className={style.errorNote}>Briefings nicht erreichbar: {error}</div>}
        {!loading && !error && scoped.length === 0 && (
          <div className={style.note}>
            Für diese Organisation gibt es an diesem Tag nichts zu melden – alles im grünen Bereich.
          </div>
        )}
        {!loading && !error && usingFallback && (
          <div className={style.fallback}>
            Keine Briefings unter der ausgewählten Organisation – es werden alle {allBriefings.length} Briefings dieses
            Tages angezeigt.
          </div>
        )}
        {!loading && !error && scoped.length > 0 && rows.length === 0 && (
          <div className={style.note}>Keine Treffer für die aktuelle Suche/Filter.</div>
        )}
        {!loading && !error && rows.length > 0 && (
          <table className={style.table}>
            <thead>
              <tr>
                <th className={style.colSev} />
                <th>ZSB</th>
                <th className={style.colCat}>Kategorie</th>
                <th>Meldung</th>
                <th>Details</th>
                <th>Empfehlung</th>
                <th className={style.colSrc}>Quelle</th>
                <th className={style.colRating}>Nützlich?</th>
              </tr>
            </thead>
            {/* One tbody per category, so a manager reads goals first and data caveats last instead
                of one undifferentiated list. */}
            {groups.map((group) => (
              <tbody key={group.title}>
                <tr className={style.groupRow}>
                  <td className={style.groupCell} colSpan={8}>
                    {group.title} ({group.items.length})
                  </td>
                </tr>
                {/* An org can have several findings on the same day, so the row identity has to
                    include the signal and the KPI — orgKey alone is no longer unique. */}
                {group.items.map((b) => {
                  const key = briefingKey(b);
                  const rated = feedback[key];
                  return (
                    <tr key={key}>
                      <td className={style.colSev}>
                        <span className={`${style.dot} ${sevDot(b.severity)}`} title={b.severity} />
                      </td>
                      <td className={style.org} title={b.orgKey}>
                        <button
                          type='button'
                          className={style.orgLink}
                          title='Im Dashboard öffnen'
                          onClick={() => openInDashboard(b.orgKey)}>
                          {orgDisplay(b.orgKey)}
                        </button>
                      </td>
                      <td className={style.colCat}>
                        {b.signalType && (
                          <span className={style.badge} title={b.kpiId || b.signalType}>
                            {signalLabel(b.signalType)}
                          </span>
                        )}
                      </td>
                      <td className={style.headline}>{b.headline}</td>
                      <td className={style.reason}>
                        {b.reason}
                        {enrichmentChips(b).length > 0 && (
                          <div className={style.chips}>
                            {enrichmentChips(b).map((chip) => (
                              <span key={chip} className={style.chip}>
                                {chip}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className={style.reco}>{b.recommendation}</td>
                      <td className={style.colSrc}>
                        <span className={style.badge}>{b.source === 'AI' ? 'KI' : 'Vorlage'}</span>
                      </td>
                      <td className={style.colRating}>
                        {rated === 'sent' && <span className={style.ratingDone}>Danke</span>}
                        {rated === 'failed' && <span className={style.ratingFailed}>Fehler</span>}
                        {(!rated || rated === 'pending') && (
                          <span className={style.rating}>
                            <button
                              type='button'
                              className={style.ratingButton}
                              disabled={rated === 'pending'}
                              title='Hilfreich'
                              onClick={() => rate(b, { useful: true })}>
                              👍
                            </button>
                            <button
                              type='button'
                              className={style.ratingButton}
                              disabled={rated === 'pending'}
                              title='Nicht hilfreich'
                              onClick={() => rate(b, { useful: false })}>
                              👎
                            </button>
                            <button
                              type='button'
                              className={style.ratingButton}
                              disabled={rated === 'pending'}
                              title='Für mich nicht relevant'
                              onClick={() => rate(b, { notRelevant: true })}>
                              ∅
                            </button>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            ))}
          </table>
        )}
      </div>
    </div>
  );
};

export default BriefingsPage;
